import PropTypes from 'prop-types';
import React, {
  useEffect, useCallback, useState, useRef, useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import useEventListener from '../../../hooks/useEventListener';
import usePluginHandler from '../../../hooks/usePluginHandler';
import { restoreLastOutside } from '../../../models/clickOut';
import { PluginMessageTypes } from '../../../models/constants';
import MessagesPlugin from '../../../models/messages';
import { NOOP } from '../../../utils/const';

export const ZINDEX_MODALIZER = 100000;

function useBodyChild() {
  const childRef = useRef();
  useEffect(() => {
    const modalizerEl = document.createElement('div');
    childRef.current = modalizerEl;
    document.body.appendChild(modalizerEl);
    return () => {
      document.body.removeChild(modalizerEl);
    };
  }, []);

  return childRef.current;
}

const IFrameModalizer = React.forwardRef(({ children, clickFromRef, className }, ref) => {
  const child = useBodyChild();
  const [modalizer, setModalizer] = useState({ active: false });

  const onClick = useCallback(() => {
    if (modalizer.active) {
      MessagesPlugin.postMessageToChilds(PluginMessageTypes.MODALIZER_CLICK);
    }
  }, [modalizer.active]);

  const messageHandler = useCallback((message) => {
    if (message.type === PluginMessageTypes.MODALIZER) {
      // il payload potrebbe essere un boolean oppure una
      // struttura più complessa in cui potrebbe essere
      // esplicitata la classe di overlay da utilizzare
      if (typeof message.payload === 'boolean') {
        setModalizer({ active: !!message.payload });
      } else {
        setModalizer(message.payload);
      }
    } else if (message.type === PluginMessageTypes.CLICK_OUTSIDE_RESTORE) {
      window.focus();
      restoreLastOutside(message.payload);
    }
  }, []);
  usePluginHandler(messageHandler);

  // permette la chiata all'evento click (guidato dalla gestione clickOutside esterna)
  useImperativeHandle(ref, () => ({
    click: clickFromRef ? onClick : NOOP,
  }), [clickFromRef, onClick]);

  useEventListener('click', clickFromRef ? NOOP : onClick, true, 'modalizer');

  return (
    <React.Fragment>
      <div
        className={className}
        style={modalizer.active ? { zIndex: `${ZINDEX_MODALIZER + 1}`, position: 'relative', height: '100%' } : { display: 'contents' }}
      >
        {children}
      </div>
      {modalizer.active && createPortal(
        <div className={modalizer.className || 'modal--overlay'} style={{ zIndex: `${ZINDEX_MODALIZER}` }} data-testid="modalOverlay" />,
        child,
      )}
    </React.Fragment>
  );
});

IFrameModalizer.propTypes = {
  /** Contenuto da renderizzare all'interno del plugin */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  /** Classe CSS del componente */
  className: PropTypes.string,

  /** Parametro utilizzato per permettere indicare se gli eventi click devono essere gestiti dall'esterno
   * via ref oppure attraverso un evento di window
   */
  clickFromRef: PropTypes.bool.isRequired,
};

IFrameModalizer.defaultProps = {
  className: undefined,
  children: null,
};

export default IFrameModalizer;
